import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import {
  formatCompactAmount,
  formatCompactAmountForCurrency,
  formatDateShortMonth,
} from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import { CompanyDTO, CompanyFundingRound } from "@shaarp/common";
import {
  BanknoteIcon,
  CalendarIcon,
  ExternalLinkIcon,
  HashIcon,
  TrendingUpIcon,
  UsersIcon,
} from "lucide-react";

function parseFundingAmount(round: CompanyFundingRound): number | null {
  if (!round.amountRaised) return null;
  const parsed = Number(round.amountRaised.replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

type CardCompanyFundingProps = {
  company: CompanyDTO;
  /** Wrap content in a Card with icon + title header. */
  standalone?: boolean;
};

export default function CardCompanyFunding({
  company,
  standalone = false,
}: CardCompanyFundingProps) {
  const { t, i18n } = useTranslation();
  const { funding, business, links } = company;

  const fundingRounds = (business?.fundingRounds ?? [])
    .slice()
    .sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  const hasRoundHistory = fundingRounds.length > 0;
  const crunchbaseUrl = links?.crunchbase;

  if (!funding.hasFundraised && !hasRoundHistory && !crunchbaseUrl) return null;

  const hasLatestRound = !!funding.latestRoundType;
  const hasRoundsCount =
    funding.totalRoundsCount != null && funding.totalRoundsCount > 0;
  const hasTotalFunding =
    funding.totalFunding != null && funding.totalFunding > 0;
  const hasLatestRoundAmount =
    funding.latestRoundAmount != null && funding.latestRoundAmount > 0;
  const hasInvestors = funding.investors.length > 0;

  const hasAnyMetric =
    hasTotalFunding || hasLatestRound || hasRoundsCount || hasInvestors;
  if (!hasAnyMetric && !hasRoundHistory && !crunchbaseUrl) return null;

  const latestRoundParts: string[] = [];
  if (hasLatestRound) {
    latestRoundParts.push(funding.latestRoundType!);
    if (hasLatestRoundAmount) {
      latestRoundParts.push(formatCompactAmount(funding.latestRoundAmount!));
    }
    if (funding.latestRoundDate) {
      latestRoundParts.push(
        `(${new Date(funding.latestRoundDate).getFullYear()})`,
      );
    }
  }

  const metrics = (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
      {/* Total raised */}
      {hasTotalFunding && (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
            <BanknoteIcon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground font-medium">
              {t("company.insights.funding.totalRaised")}
            </span>
            <span className="text-base font-bold leading-snug">
              {formatCompactAmount(funding.totalFunding!)}
            </span>
          </div>
        </div>
      )}

      {/* Separator */}
      {hasTotalFunding && (hasLatestRound || hasRoundsCount) && (
        <div className="h-10 w-px bg-border" />
      )}

      {/* Latest round */}
      {hasLatestRound && (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
            <TrendingUpIcon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground font-medium">
              {t("company.insights.funding.latestRound")}
            </span>
            <span className="text-base font-bold leading-snug">
              {latestRoundParts.join(" · ")}
            </span>
          </div>
        </div>
      )}

      {/* Separator */}
      {(hasTotalFunding || hasLatestRound) && hasRoundsCount && (
        <div className="h-10 w-px bg-border" />
      )}

      {/* Rounds count */}
      {hasRoundsCount && (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
            <HashIcon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground font-medium">
              {t("company.insights.funding.roundsCount")}
            </span>
            <span className="text-base font-bold leading-snug">
              {funding.totalRoundsCount}
            </span>
          </div>
        </div>
      )}

      {/* Separator */}
      {(hasTotalFunding || hasLatestRound || hasRoundsCount) &&
        hasInvestors && <div className="h-10 w-px bg-border" />}

      {/* Investors count */}
      {hasInvestors && (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
            <UsersIcon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground font-medium">
              {t("company.insights.funding.investors")}
            </span>
            <span className="text-base font-bold leading-snug">
              {funding.investors.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const investorBadges = hasInvestors && (
    <div className="flex flex-wrap gap-1.5">
      {funding.investors.map((investor) => (
        <Badge key={investor} variant="secondary">
          {investor}
        </Badge>
      ))}
    </div>
  );

  const crunchbaseLink = crunchbaseUrl && (
    <Button variant="outline" size="sm" className="w-fit" asChild>
      <a href={crunchbaseUrl} target="_blank" rel="noreferrer">
        <img
          src="https://www.crunchbase.com/favicon.ico"
          alt="Crunchbase"
          className="w-4 h-4"
        />
        {t("company.insights.funding.viewOnCrunchbase")}
        <ExternalLinkIcon className="w-3.5 h-3.5" />
      </a>
    </Button>
  );

  const roundsHistory = hasRoundHistory && (
    <div className="flex flex-col gap-3">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">
        {t("company.insights.funding.historyTitle")}
      </span>
      <ol className="relative ml-2 border-l border-border pl-5 flex flex-col gap-4">
        {fundingRounds.map((round, index) => {
          const amount = parseFundingAmount(round);
          const currency = round.amountRaisedCurrency;
          const isLatest = index === 0;
          return (
            <li
              key={`${round.date ?? "undated"}-${round.type ?? index}-${index}`}
              className="relative"
            >
              <span
                className={cn(
                  "absolute -left-[27px] top-1 flex items-center justify-center w-4 h-4 rounded-full ring-2 ring-background",
                  isLatest
                    ? "bg-gradient-to-br from-emerald-400 to-emerald-600"
                    : "bg-muted-foreground/40",
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    isLatest ? "bg-white" : "bg-background",
                  )}
                />
              </span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {round.type && (
                  <span className="text-sm font-semibold">{round.type}</span>
                )}
                {amount != null ? (
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCompactAmountForCurrency(amount, currency)}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground italic">
                    {t("company.insights.funding.undisclosedAmount")}
                  </span>
                )}
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" />
                  {round.date
                    ? formatDateShortMonth(round.date, i18n.language)
                    : t("company.insights.funding.unknownDate")}
                </span>
              </div>
              {round.leadInvestors.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">
                    {t("company.insights.funding.leadInvestors")}:
                  </span>
                  {round.leadInvestors.map((inv) => (
                    <Badge
                      key={inv}
                      variant="outline"
                      className="text-[11px] py-0 px-1.5"
                    >
                      {inv}
                    </Badge>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );

  if (standalone) {
    return (
      <Card>
        <CardHeader className="pb-3 sm:pb-4">
          <CardTitleWithIcon icon={BanknoteIcon} title={t("company.insights.funding.title")} />
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {hasAnyMetric && metrics}
          {investorBadges}
          {roundsHistory}
          {crunchbaseLink}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {hasAnyMetric && (
        <div className="rounded-xl border border-border bg-background px-5 py-4">
          {metrics}
        </div>
      )}
      {investorBadges}
      {roundsHistory}
      {crunchbaseLink}
    </div>
  );
}
